import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Chip, Divider, Stack, ToggleButton, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded'
import { apiGet } from '../../generic/Api_Functions'
import { GetAccumulatedUtcTime } from '../../generic/DateTimeFunctions'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { applyNodeSelection } from './bomTree'
import { buildCoverageContext } from './processTreeCoverage'
import {
  PROCESS_TREE_ROW_KIND,
  collectProcessTotals,
  collectRequiredPredecessorIds,
  containingSetKeys,
  resolveTopLevelNodeId,
  selectVisibleRows
} from './processTree'
import ProcessTreeRow, { COMPACT_ROW, indentOf } from './ProcessTreeRow'
import { useTreeFolding } from './useTreeFolding'
import {
  DROP_TARGET_KIND,
  EMPTY_SPACE_DROP_TARGET,
  MINI_TREE_TAG,
  MINI_TREE_WARNING_KIND,
  NO_DROP_TARGET,
  buildMiniTreeRows,
  collectNodeTypes,
  miniTreeTagOf,
  resolveRowDropTarget
} from './miniTreeRows'

/**
 * ProcessMiniTree — story 167 (HU-03, HU-06 · RN-05, RN-08). The process tree of the panel: Tree's own
 * reading of the workflow being edited (unsaved changes included), simplified to its structure, a SUB/OP tag
 * per row (D1), the shared selection with the canvas, what a double click opens (the accordions of the editor or the
 * subworkflow window; an operation inside a SUB opens nothing — PO, 29/09/2026), the Materials
 * button, and the rows as drop targets (D4, D5) with their insertion line.
 *
 * During a drag it reports the target of the row under the cursor only when that row CHANGES, never on
 * every `dragover` (R1): the rows are memoized and the line moves by re-rendering two of them.
 */
const EMPTY_BOM = { loaded: false, loading: false, lines: [] }
const SELECTABLE_KINDS = new Set([PROCESS_TREE_ROW_KIND.JOB, PROCESS_TREE_ROW_KIND.SET])
// RN-05.11 (S121): a material row is clicked as in Tree (`ProcessTreeRow.jsx:81-86`, `:432-435`) — it has no
// node of its own, so it is clickable but never a canvas target (SELECTABLE_KINDS stays the node rows).
const CLICKABLE_KINDS = new Set([PROCESS_TREE_ROW_KIND.JOB, PROCESS_TREE_ROW_KIND.SET, PROCESS_TREE_ROW_KIND.MATERIAL])
const noop = () => {}

/**
 * PO, 29/09/2026: the tree of the panel is the Tree of the workflow in small — the same row (`ProcessTreeRow`,
 * compact), with its zebra, its selection, its chevron, its branch column, its chips and what each operation lasts,
 * so the two trees read the same. What the panel adds: the SUB/OP tag at the right end (D1), the drop target with its
 * insertion line (D4, D5) and the warning line of a sub-process that could not be loaded (E-16).
 */
const TAG_COLOR = { [MINI_TREE_TAG.SUB]: 'success', [MINI_TREE_TAG.OP]: 'info' }

const MiniTreeTag = ({ tag }) => (tag
  ? (
    <Chip
      size='small'
      variant='outlined'
      color={TAG_COLOR[tag]}
      data-testid='mini-tree-tag'
      label={tag}
      sx={{ height: 16, fontSize: 9.5, fontWeight: 700, letterSpacing: '.06em', '& .MuiChip-label': { px: 0.6 } }}
    />
    )
  : null)

const InsertionLine = () => (
  <Box
    data-testid='mini-tree-insertion-line'
    sx={{ position: 'absolute', left: 10, right: 4, bottom: -2, height: 3, zIndex: 1, bgcolor: 'primary.main', borderRadius: 1, pointerEvents: 'none', '&::before': { content: '""', position: 'absolute', left: -6, top: -3, width: 9, height: 9, borderRadius: '50%', bgcolor: 'primary.main' } }}
  />
)

const MiniTreeRowComponent = ({ row, tag, entering, enterIndex, leaving, leaveIndex, alt, showDuration, selected, dependency, collapsed, insertionLine, onClickRow, onDoubleClickRow, onToggleRow, onDragOverRow, onDropRow, onExpandMaterial, onJumpToNode }) => {
  const { t_ } = useSmartTranslation()
  const rootProps = {
    onDoubleClick: () => onDoubleClickRow(row),
    onDragOver: (event) => onDragOverRow(event, row),
    onDrop: (event) => onDropRow(event, row)
  }
  if (row.kind === MINI_TREE_WARNING_KIND) {
    return (
      <Box
        {...rootProps}
        data-testid='mini-tree-row'
        data-key={row.key}
        data-kind={row.kind}
        data-node-id={row.nodeId}
        sx={{ display: 'flex', alignItems: 'center', height: 24, flexShrink: 0, pr: 1, backgroundColor: alt ? 'background.row2' : undefined }}
        style={{ paddingLeft: 2 + COMPACT_ROW.branchColPx + indentOf(row.depth) + COMPACT_ROW.foldPx + 4 }}
      >
        <Typography component='span' noWrap data-testid='mini-tree-warning' sx={{ fontSize: 11.5, color: 'warning.main' }}>
          {row.message ? t_('This subprocess could not be loaded: {{message}}', { message: row.message }) : t_('This subprocess could not be loaded')}
        </Typography>
      </Box>
    )
  }
  return (
    <ProcessTreeRow
      compact
      testIdPrefix='mini-tree'
      rootProps={rootProps}
      row={row}
      alt={alt}
      showDuration={showDuration}
      selected={selected}
      dependency={dependency}
      collapsed={collapsed}
      // A click selects; what opens is the double click (`onDoubleClickRow`), not a second click.
      onSelect={onClickRow}
      onOpen={noop}
      onToggle={onToggleRow}
      onExpandMaterial={onExpandMaterial}
      onJumpToNode={onJumpToNode}
      trailing={<MiniTreeTag tag={tag} />}
      overlay={insertionLine ? <InsertionLine /> : null}
      entering={entering}
      enterIndex={enterIndex}
      leaving={leaving}
      leaveIndex={leaveIndex}
    />
  )
}

const MiniTreeRow = React.memo(MiniTreeRowComponent)

const ProcessMiniTree = ({
  workflow = {},
  effectiveWorkflow = {},
  failedSetNodeIds = [],
  failedSetMessages = {},
  currentNodeId = '',
  dispatchWorkflowData = noop,
  nodes = [],
  setNodes = noop,
  onSubWorkflowDoubleClick = noop,
  showAlert = noop,
  dragActive = false,
  dropTargetRowKey = '',
  dropTargetKind = DROP_TARGET_KIND.NONE,
  onDropTargetChange = noop,
  onDropAfter = noop,
  onOpenJobProperties = noop,
  onOpenJobResources = noop
}) => {
  const { t_ } = useSmartTranslation()
  const isUserAuthorized = useAuthorization()
  const canSeeMaterial = isUserAuthorized('RESOURCES')
  const canSeeJobs = isUserAuthorized('WORKFLOW_JOBS')

  const [openSetKeys, setOpenSetKeys] = useState(() => new Set())
  const [selectedRowKey, setSelectedRowKey] = useState('')
  const [materialEnabled, setMaterialEnabled] = useState(false)
  const [expandedMaterialKeys, setExpandedMaterialKeys] = useState(() => new Set())
  const [bom, setBom] = useState(EMPTY_BOM)
  const reportedTargetRef = useRef('')

  const coverage = useMemo(
    () => (materialEnabled && bom.loaded ? buildCoverageContext(bom.lines, effectiveWorkflow?.nodes) : null),
    [materialEnabled, bom.loaded, bom.lines, effectiveWorkflow]
  )

  // E-10 (CA8.2): if the projection itself breaks, the zone says so and the library keeps working.
  const projection = useMemo(() => {
    try {
      return { rows: buildMiniTreeRows({ workflow: effectiveWorkflow, coverage, failedSetNodeIds, failedSetMessages, expandedMaterialKeys }), failed: false }
    } catch (thrown) {
      return { rows: [], failed: true }
    }
  }, [effectiveWorkflow, coverage, failedSetNodeIds, failedSetMessages, expandedMaterialKeys])
  const rows = projection.rows

  const nodeTypeById = useMemo(() => collectNodeTypes(effectiveWorkflow?.nodes), [effectiveWorkflow])
  const isCollapsed = useCallback((row) => row.kind === PROCESS_TREE_ROW_KIND.SET && !openSetKeys.has(row.key), [openSetKeys])
  const { visibleRows } = useMemo(() => selectVisibleRows(rows, { isCollapsed, query: '', materialEnabled }), [rows, isCollapsed, materialEnabled])

  // A material row has no node: it stands for the operation that consumes it (`ProcessTreeView.jsx:336-338`).
  const nodeRowOf = useCallback((row) => (row && row.kind === PROCESS_TREE_ROW_KIND.MATERIAL
    ? rows.find((candidate) => candidate.key === row.parentKey) || null
    : row), [rows])

  // RN-05.7 (DL-11): the canvas selection marks its row, opening the SUBs that contain it.
  useEffect(() => {
    if (!currentNodeId) {
      setSelectedRowKey('')
      return
    }
    // RN-05.11: a marked material line that stops being shown (Materials off, its material folded) hands
    // the mark to its operation, as Tree lifts it (`ProcessTreeView.jsx:750-770`).
    const materialOwnerKey = selectedRowKey.indexOf('/mat/') > 0 ? selectedRowKey.slice(0, selectedRowKey.indexOf('/mat/')) : ''
    if (materialOwnerKey && !visibleRows.some((row) => row.key === selectedRowKey)) {
      setSelectedRowKey(materialOwnerKey)
      return
    }
    const selectedRow = nodeRowOf(rows.find((row) => row.key === selectedRowKey))
    if (selectedRow && resolveTopLevelNodeId(selectedRow.nodeId, effectiveWorkflow?.nodes) === currentNodeId) {
      return
    }
    const target = rows.find((row) => row.nodeId === currentNodeId && SELECTABLE_KINDS.has(row.kind))
    if (!target) {
      return
    }
    const ancestors = containingSetKeys(target, new Map(rows.map((row) => [row.key, row])))
    if (ancestors.length > 0) {
      setOpenSetKeys((previous) => new Set([...previous, ...ancestors]))
    }
    setSelectedRowKey(target.key)
  }, [currentNodeId, rows, visibleRows, selectedRowKey, effectiveWorkflow, nodeRowOf])

  /**
   * RN-05.6: the row is selected at any depth; the canvas marks its first-level node, patched, never rebuilt.
   * RN-05.11: a material row stays marked and the canvas follows the operation that consumes it.
   */
  const selectRow = useCallback((row) => {
    setSelectedRowKey(row.key)
    const nodeRow = nodeRowOf(row)
    if (!nodeRow) {
      return
    }
    const topLevelId = resolveTopLevelNodeId(nodeRow.nodeId, effectiveWorkflow?.nodes)
    const topLevelNode = (workflow?.nodes || []).find((node) => node.id === topLevelId)
    if (!topLevelNode) {
      return
    }
    dispatchWorkflowData({ type: 'SET_CURRENT_NODE', node: topLevelNode })
    setNodes(applyNodeSelection(nodes, topLevelId))
  }, [nodeRowOf, effectiveWorkflow, workflow, dispatchWorkflowData, setNodes, nodes])

  const clickRow = useCallback((row) => {
    if (row.kind === PROCESS_TREE_ROW_KIND.REFERENCE) {
      const developed = rows.find((candidate) => candidate.nodeId === row.nodeId && SELECTABLE_KINDS.has(candidate.kind))
      if (developed) {
        const ancestors = containingSetKeys(developed, new Map(rows.map((candidate) => [candidate.key, candidate])))
        setOpenSetKeys((previous) => new Set([...previous, ...ancestors]))
        selectRow(developed)
      }
      return
    }
    if (!CLICKABLE_KINDS.has(row.kind)) {
      return
    }
    selectRow(row)
  }, [rows, selectRow])

  /**
   * PO, 29/09/2026: a double click opens what the row is about, where the editor shows it — an OP its "General
   * Properties" accordion, a material line the "Resources" accordion of its operation, a first-level SUB the
   * subworkflow window. Those accordions only show first-level operations, so what lies inside a SUB opens nothing:
   * the subworkflow windows lead to it.
   */
  const doubleClickRow = useCallback((row) => {
    if (row.kind === PROCESS_TREE_ROW_KIND.SET) {
      const topLevelSet = (workflow?.nodes || []).find((node) => node.id === row.nodeId)
      if (topLevelSet) {
        onSubWorkflowDoubleClick({}, { id: topLevelSet.id, data: { referenceId: topLevelSet.subWorkflow?.id } })
      }
      return
    }
    const jobRow = nodeRowOf(row)
    if (!jobRow || jobRow.kind !== PROCESS_TREE_ROW_KIND.JOB) {
      return
    }
    if (resolveTopLevelNodeId(jobRow.nodeId, effectiveWorkflow?.nodes) !== jobRow.nodeId) {
      return
    }
    if (row.kind === PROCESS_TREE_ROW_KIND.MATERIAL) {
      onOpenJobResources()
    } else {
      onOpenJobProperties()
    }
  }, [workflow, effectiveWorkflow, nodeRowOf, onSubWorkflowDoubleClick, onOpenJobProperties, onOpenJobResources])

  // PO, 29/09/2026: a SUB opens and closes as in the Tree — its rows fall into place, and leave rising the other way.
  const { displayedRows, enterIndexByKey, leaveIndexByKey, setOpened, setClosed } = useTreeFolding(visibleRows)
  const toggleRow = useCallback((row) => {
    const closing = openSetKeys.has(row.key)
    setOpenSetKeys((previous) => {
      const next = new Set(previous)
      if (next.has(row.key)) {
        next.delete(row.key)
      } else {
        next.add(row.key)
      }
      return next
    })
    if (closing) {
      setClosed(row)
      return
    }
    setOpened(row)
  }, [openSetKeys, setOpened, setClosed])

  const expandMaterial = useCallback((row) => {
    setExpandedMaterialKeys((previous) => {
      const next = new Set(previous)
      if (next.has(row.materialOwnerKey)) {
        next.delete(row.materialOwnerKey)
      } else {
        next.add(row.materialOwnerKey)
      }
      return next
    })
  }, [])

  /** RN-05.10 / E-11: the BOM is asked the first time it is needed, and only then. */
  const loadBom = useCallback(async () => {
    if (bom.loaded) {
      return true
    }
    if (bom.loading) {
      return false
    }
    setBom((previous) => ({ ...previous, loading: true }))
    try {
      const lines = await apiGet('workflowResource/' + workflow?.tempWorkflowUUID + (workflow?.id ? '/' + workflow.id : ''))
      setBom({ loaded: true, loading: false, lines: Array.isArray(lines) ? lines : [] })
      return true
    } catch (thrown) {
      setBom(EMPTY_BOM)
      showAlert({ title: t_('Error loading the BOM'), severity: 'error', message: '' })
      return false
    }
  }, [bom.loaded, bom.loading, workflow, showAlert, t_])

  const toggleMaterial = useCallback(async () => {
    if (materialEnabled) {
      setMaterialEnabled(false)
      setExpandedMaterialKeys(new Set())
      return
    }
    setMaterialEnabled(await loadBom())
  }, [materialEnabled, loadBom])

  // --- drop targets (RN-08.8, RN-08.9) -------------------------------------------------------------
  useEffect(() => {
    if (!dragActive) {
      reportedTargetRef.current = ''
    }
  }, [dragActive])

  const reportTarget = useCallback((target) => {
    const signature = target.kind + ':' + target.rowKey
    if (reportedTargetRef.current === signature) {
      return
    }
    reportedTargetRef.current = signature
    onDropTargetChange(target)
  }, [onDropTargetChange])

  const dragOverRow = useCallback((event, row) => {
    if (!dragActive) {
      return
    }
    event.stopPropagation()
    const target = resolveRowDropTarget(row, workflow?.nodes, effectiveWorkflow?.nodes)
    if (target.kind === DROP_TARGET_KIND.AFTER) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
    }
    reportTarget(target)
  }, [dragActive, workflow, effectiveWorkflow, reportTarget])

  const dropOnRow = useCallback((event, row) => {
    event.preventDefault()
    event.stopPropagation()
    const target = resolveRowDropTarget(row, workflow?.nodes, effectiveWorkflow?.nodes)
    if (target.kind === DROP_TARGET_KIND.AFTER) {
      onDropAfter(event, target.nodeId)
    }
  }, [workflow, effectiveWorkflow, onDropAfter])

  const dragOverEmptySpace = () => {
    if (dragActive) {
      reportTarget(EMPTY_SPACE_DROP_TARGET)
    }
  }

  const leaveTree = (event) => {
    if (!dragActive || event.currentTarget.contains(event.relatedTarget)) {
      return
    }
    reportedTargetRef.current = ''
    onDropTargetChange(NO_DROP_TARGET)
  }

  const insertionRowKey = dropTargetKind === DROP_TARGET_KIND.AFTER ? dropTargetRowKey : ''
  // As in Tree: the operations the selected one needs finished before it — every OK predecessor at any distance —
  // are painted with it. A marked material line speaks for its operation (`ProcessTreeView.jsx` inspectedRow).
  const inspectedRow = nodeRowOf(rows.find((row) => row.key === selectedRowKey))
  const dependencyIds = useMemo(
    () => (inspectedRow && SELECTABLE_KINDS.has(inspectedRow.kind) ? collectRequiredPredecessorIds(inspectedRow.nodeId, effectiveWorkflow?.nodes) : new Set()),
    [inspectedRow, effectiveWorkflow]
  )
  const isDependencyRow = (row) => row.nodeId !== '' && row.nodeId !== inspectedRow?.nodeId && dependencyIds.has(row.nodeId) &&
    (SELECTABLE_KINDS.has(row.kind) || row.kind === PROCESS_TREE_ROW_KIND.REFERENCE)
  // A reference row sends to the row where its operation is developed, as a click on it always did.
  const jumpToNode = useCallback((nodeId) => clickRow({ kind: PROCESS_TREE_ROW_KIND.REFERENCE, nodeId }), [clickRow])
  // The figures of the Tree's toolbar: how many operations the whole process has and what they last.
  const totals = useMemo(() => collectProcessTotals(effectiveWorkflow, { failedSetNodeIds }), [effectiveWorkflow, failedSetNodeIds])

  return (
    <Stack data-testid='process-mini-tree' sx={{ flex: '1 1 0', minHeight: 0 }} onDragLeave={leaveTree}>
      {/* The toolbar of the Tree, in small: the figures of the whole process on the left, Materials on the right. */}
      <Stack direction='row' alignItems='center' spacing={1} sx={{ flex: '0 0 auto', pl: 1.5, pr: 1, py: 0.5, minHeight: 38, borderBottom: 1, borderColor: 'divider' }}>
        <Stack data-testid='mini-tree-header' direction='row' spacing={1} alignItems='baseline' sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography noWrap data-testid='mini-tree-total-count' sx={{ fontSize: 12.5, fontWeight: 600, fontFamily: 'monospace' }}>
            {t_('{{count}} operations', { count: totals.jobCount })}
          </Typography>
          {canSeeJobs
            ? (
              <>
                <Divider orientation='vertical' flexItem />
                <Typography noWrap data-testid='mini-tree-total-time' sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {t_('Total time')}: <Box component='span' sx={{ fontFamily: 'monospace', color: 'text.primary' }}>{GetAccumulatedUtcTime(totals.totalTimeMs)}</Box>
                </Typography>
              </>
              )
            : null}
        </Stack>
        {canSeeMaterial
          ? (
            <Tooltip title={t_('Materials')}>
              <ToggleButton
                size='small'
                value='materials'
                selected={materialEnabled}
                data-testid='mini-tree-materials-toggle'
                aria-label={t_('Materials')}
                onChange={toggleMaterial}
                // On, the icon turns to the green of MIC on a raised surface — no halo (PO, 29/09/2026).
                sx={{
                  px: 0.75,
                  py: 0.25,
                  color: 'text.secondary',
                  '& svg': { fontSize: 16 },
                  '&.Mui-selected, &.Mui-selected:hover': {
                    color: 'primary.main',
                    borderColor: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.common.white, 0.08),
                    boxShadow: (theme) => '0 1px 4px ' + alpha(theme.palette.common.black, 0.45)
                  }
                }}
              >
                <CategoryRoundedIcon fontSize='inherit' />
              </ToggleButton>
            </Tooltip>
            )
          : null}
      </Stack>
      {projection.failed
        ? <Alert severity='warning' data-testid='mini-tree-failed' sx={{ m: 1 }}>{t_('The process tree could not be shown')}</Alert>
        : (
          <Box data-testid='mini-tree-rows' sx={{ flex: '1 1 0', minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', px: 0.5, py: 0.5 }}>
            {displayedRows.map((row, index) => (
              <MiniTreeRow
                key={row.key}
                row={row}
                entering={enterIndexByKey.has(row.key)}
                enterIndex={enterIndexByKey.get(row.key) || 0}
                leaving={leaveIndexByKey.has(row.key)}
                leaveIndex={leaveIndexByKey.get(row.key) || 0}
                alt={index % 2 === 1}
                showDuration={canSeeJobs}
                onJumpToNode={jumpToNode}
                tag={miniTreeTagOf(row, nodeTypeById)}
                selected={row.key === selectedRowKey}
                dependency={isDependencyRow(row)}
                collapsed={isCollapsed(row)}
                insertionLine={row.key === insertionRowKey}
                onClickRow={clickRow}
                onDoubleClickRow={doubleClickRow}
                onToggleRow={toggleRow}
                onDragOverRow={dragOverRow}
                onDropRow={dropOnRow}
                onExpandMaterial={expandMaterial}
              />
            ))}
            {/* DL-07: the empty space under the last row is not a target, and says why. */}
            <Box data-testid='mini-tree-empty-space' onDragOver={dragOverEmptySpace} sx={{ flex: '1 0 24px' }} />
          </Box>
          )}
    </Stack>
  )
}

export default React.memo(ProcessMiniTree)
